import type {Room} from '../../types/room'

/**
 * RAA has no roomless flag, so the name is the only signal: empty for new
 * offers, or the legacy placeholder until the BE rollout finishes. Route every
 * check through here so only this file changes once a flag lands.
 */
const LEGACY_ROOMLESS_ROOM_NAME = 'Your room'

/** Whether the name needs one generated for display. */
export const isUnnamedRoomName = (roomName?: string | null): boolean =>
  !roomName

/**
 * Whether a room name marks a roomless room rather than a real one.
 * @param roomName - Name as it arrives from RAA, BoVio or the offer check.
 */
export const isRoomlessRoomName = (roomName?: string | null): boolean =>
  isUnnamedRoomName(roomName) || roomName === LEGACY_ROOMLESS_ROOM_NAME

/** A missing room is not roomless, even though a missing name is. */
export const isRoomlessRoom = (room?: Pick<Room, 'name'>): boolean =>
  Boolean(room) && isRoomlessRoomName(room?.name)

/**
 * Roomless experiment variant assigned by RAA, carried to checkout as `rl`.
 * V1 shows the real room on payment, V2 keeps it hidden until booking.
 */
export enum RoomlessVersion {
  V1 = '1',
  V2 = '2'
}

// TODO [sc-198186]: read the version from RAA's own field once the room name
// is translated and no longer marks it.
/**
 * Reads RAA's assignment off the selected room. Only the accommodation page
 * may call this: payment trusts `rl` alone because BoVio returns the real name.
 */
export const getRoomlessVersion = (
  room?: Pick<Room, 'name'>
): RoomlessVersion | undefined => {
  if (!isRoomlessRoom(room)) return undefined
  return isUnnamedRoomName(room?.name) ? RoomlessVersion.V2 : RoomlessVersion.V1
}

/** Reads `rl` from the checkout URL. Unknown values mean an ordinary offer. */
export const parseRoomlessVersion = (
  rl?: string | null
): RoomlessVersion | undefined =>
  Object.values(RoomlessVersion).find(version => version === rl)
